<template>
  <f-box class="stat p-7 has-row-3 has-no-col-gap has-no-row-gap" grid>
    <p class="is-col-8 color-gray">
      <b>
        <small>{{ title }}</small>
      </b>
    </p>

    <p class="is-col-4 is-align-right color-placeholder">
      <b>
        <small>{{ currentYear }}</small>
      </b>
    </p>

    <div class="is-col-6 m-0">
      <h6 class="color-blue-dark">{{ displayAmount(ca) }} €</h6>
    </div>

    <div v-if="month === null" class="stat-ingoings-chart is-col-6 is-row-2">
      <div class="chart-container" ref="chartContainer"></div>
    </div>

    <div v-if="month !== null" class="stat-ingoings-percentage is-col-6 is-row-2">
      <h5
        :class="[
          'm-0',
          {
            'color-success': evolution.increase,
            'color-warning': evolution.decrease,
          }
        ]"
      >{{ evolution.percent }}</h5>

      <p v-if="month && evolution.applicable">
        <small>Par rapport à {{ evolution.month }}</small>
      </p>
    </div>

    <div class="is-col-6">
      <p>
        <small>+ {{ displayAmount(vat) }} € (TVA)</small>
      </p>
    </div>
  </f-box>
</template>

<script>
import { onMounted, nextTick, ref, onUnmounted, computed, watch } from 'vue'
import useStatistics from '@composables/statistics/use-statistics.js'
import useNumbers from '@composables/use-numbers'
import Chart from 'apexcharts'

export default {
  props: {
    year: {
      type: [Number, null],
      default: null,
    },

    month: {
      type: [Number, null],
      default: null,
    },

    from: {
      type: [Date, null],
      default: null,
    },

    to: {
      type: [Date, null],
      default: null,
    },

    label: {
      type: [String, null],
      default: null,
    },
  },

  setup (props) {
    const { displayAmount, isNumber } = useNumbers()
    const {
      ca,
      caByMonth,
      vat,
      period,
      evolution,
      year: currentYear
    } = useStatistics(props)

    const title = computed(() => 
      props.label !== null ? props.label : period.value.formatted
    )

    const displayChart = computed(() => props.month === null)
    
    const chartContainer = ref(null)
    const chartOptions = computed(() => {
      return {
        chart: {
          type: 'line',
          width: chartContainer.value?.offsetWidth - 2,
          height: chartContainer.value?.offsetHeight - 2,
          redrawOnParentResize: false,
          zoom: {
            enabled: false
          },
          toolbar: {
            show: false
          },
          sparkline: {
            enabled: true
          }
        },
        colors: ['#6fbbff'],
        dataLabels: {
          enabled: false
        },
        series: [
          {
            name: 'Encaissements',
            data: caByMonth.value || []
          }
        ],
        stroke: {
          curve: 'smooth',
          width: 1
        },
        yaxis: {
          labels: {
            show: false
          }
        },
        xaxis: {
          labels: {
            show: false
          }
        },
        legend: {
          show: false
        },
        tooltip: {
          // enabled: false,
          custom: function({ series, seriesIndex, dataPointIndex, w }) {
            return `
                <div class="stat-detail">
                  <p>
                    <b>
                      <small>
                        ${displayAmount(series[seriesIndex][dataPointIndex])} €
                      </small>
                    </b>
                    <small>
                      (${period?.value?.months[dataPointIndex] || ''})
                    </small>
                  </p>
                </div>
              `
          }
        }
      }
    })

    let chart = null;

    onMounted(() => {
      nextTick(() => {
        if (displayChart.value) {
          chart = new Chart(chartContainer.value, chartOptions.value)

          chart.render()
       } 
      })
    })

    onUnmounted(() => {
      chart && chart.destroy()
    })

    watch(
      () => chartOptions.value,
      () => {
        chart && chart.updateOptions(chartOptions.value);
      }
    )

    return {
      chartContainer,
      ca,
      caByMonth,
      vat,
      currentYear,
      title,
      evolution,
      displayAmount
    }
  }
}
</script>

<style lang="scss">
.stat {
  position: relative;

  h6,
  p {
    margin: 0;
  }
}

.stat-ingoings-chart {
  position: relative;
  height: 100%;
  width: 100%;
  cursor: pointer;
}

.chart-container {
  position: absolute;
  top: 0px;
  left: 0px;
  height: 100%;
  width: 100%;
}

.stat-detail {
  border: none;
  box-shadow: 0 1px 1px 0 rgba(102, 166, 255, 0.1);
  background: $white;
  color: $blue-dark;
  padding: 0.1rem 0.3rem;
}
</style>
